/**
 * The platform half of the unified Share action.
 *
 * Capture components stage media rather than uploading it the moment it is captured, so a
 * form submit has to flush whatever is staged before it serializes hidden inputs. That
 * turns "upload, then share" into a single participant-visible action, and it means an
 * abandoned capture never reaches S3.
 *
 * Components that have nothing staged resolve immediately, so forms without capture
 * components behave exactly as they did before.
 */

const CAPTURE_COMPONENTS = 'anear-image-upload, anear-file-upload, anear-camera'

function captureComponentsIn(form) {
  const found = form?.querySelectorAll?.(CAPTURE_COMPONENTS)
  if (!found?.length) return []
  return Array.from(found).filter((element) => typeof element.commitPendingMedia === 'function')
}

export function hasPendingMedia(form) {
  return captureComponentsIn(form).some((element) => element.hasPendingMedia === true)
}

/**
 * Flush every staged capture in the form, one at a time. Sequential rather than parallel:
 * a phone on venue wifi uploading a clip and a poster at once is slower than doing them
 * in turn, and progress reporting stays comprehensible.
 */
export async function commitPendingMedia(form) {
  for (const element of captureComponentsIn(form)) {
    await element.commitPendingMedia()
  }
}

function actionExpired(timeoutEl) {
  return Boolean(
    timeoutEl && typeof timeoutEl.isActionExpired === 'function' && timeoutEl.isActionExpired()
  )
}

/**
 * The gate a form submit passes through before it serializes and publishes.
 *
 * Shared by EventFrame and ChildEventFrame so the ordering rules live in one place:
 * the timeout is checked before the transfer, the transfer is awaited, and the timeout is
 * checked *again* afterwards because a slow upload can cross the deadline.
 *
 * @returns {Promise<{ proceed: boolean, expired: boolean, error: Error|null }>}
 */
export async function awaitPendingMedia(form, timeoutEl = null) {
  if (actionExpired(timeoutEl)) return { proceed: false, expired: true, error: null }

  if (!hasPendingMedia(form)) return { proceed: true, expired: false, error: null }

  try {
    await commitPendingMedia(form)
  } catch (error) {
    return { proceed: false, expired: false, error }
  }

  if (actionExpired(timeoutEl)) return { proceed: false, expired: true, error: null }

  return { proceed: true, expired: false, error: null }
}
